import { MONTHS } from 'util/types/months';
import { Box } from '@mui/material';
import { Button, FormErrors, Modal, ModalProps, Select } from 'components/ui';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { updateBirthdayFormSchema } from 'forms';
import { useProfileApi } from 'services/api';
import { useSnackbar } from 'notistack';
import { throttle } from 'lodash';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

export interface UpdateBirthdayModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {}

export const UpdateBirthdayModal = ({
  open,
  onClose,
}: UpdateBirthdayModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields: touched },
  } = useAppForm(updateBirthdayFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    const birthdate = new Date(profile?.birthdate);
    const month = MONTHS[birthdate.getUTCMonth()];
    const day = birthdate.getUTCDate();
    const year = birthdate.getUTCFullYear();

    reset({
      month,
      day,
      year,
    });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (data: z.TypeOf<typeof updateBirthdayFormSchema>) => {
    if (!data.day || !data.year || !data.month) return;

    setSubmitting(true);
    const date = new Date(
      Date.UTC(data.year, MONTHS.indexOf(data.month), data.day),
    );
    const result = await profileApi.updateBirthday(date);

    if (result.success) return onClose?.();

    setSubmitting(false);
    enqueueSnackbar(result.error.message, { variant: 'error' });
  };

  const throttledOnSubmit = throttle(onSubmit, 200, { leading: true });

  const handleFormSubmit = (
    data: z.TypeOf<typeof updateBirthdayFormSchema>,
  ) => {
    throttledOnSubmit(data);
  };

  const month = useWatch({
    name: 'month',
    control,
  });

  const dayOptions = useMemo(() => {
    const dayOptions = [<option key="">--</option>];

    let numDays = 31;

    if (month !== undefined) {
      numDays = Math.abs(MONTHS.indexOf(month) - 5) % 2 === 0 ? 31 : 30;
      if (month === 'February') numDays = 29;
    }

    for (let i = 1; i <= numDays; i++) {
      dayOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }

    return dayOptions;
  }, [month]);

  const yearOptions = useMemo(() => {
    const yearOptions = [<option key="">--</option>];
    for (let i = 1900; i <= new Date().getUTCFullYear(); i++) {
      yearOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }

    return yearOptions;
  }, []);

  return (
    <Modal title="Update Your Birthday" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box
          style={{ width: 600, maxWidth: '80vw' }}
          display="flex"
          alignItems="flex-end"
        >
          <Box width={140}>
            <Select
              label="Birth Date"
              hasError={touched.month && !!errors.month}
              defaultValue={month}
              {...register('month')}
            >
              <option>--</option>
              <>
                {MONTHS.map((month, i) => (
                  <option key={i} value={month}>
                    {month}
                  </option>
                ))}
              </>
            </Select>
          </Box>
          <Box width={76} ml={20}>
            <Select
              hasError={touched.day && !!errors.day}
              {...register('day', { valueAsNumber: true })}
            >
              {dayOptions}
            </Select>
          </Box>
          <Box width={94} ml={20}>
            <Select
              hasError={touched.year && !!errors.year}
              {...register('year', { valueAsNumber: true })}
            >
              {yearOptions}
            </Select>
          </Box>
        </Box>

        <FormErrors>{errors.month ? ['Please select a month'] : []}</FormErrors>
        <FormErrors>{errors.day?.message || ''}</FormErrors>
        <FormErrors>{errors.year?.message || ''}</FormErrors>

        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            type="submit"
            minWidth={100}
            variant="vibrant"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
